import { useCallback, useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Tag, Upload as UploadIcon, Download as DownloadIcon, Building2, AlertTriangle, ListOrdered } from "lucide-react";
import LoadingState from "../../components/ui/loading-state";
import { useAuth } from "../../context/AuthContext";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";

/**
 * KategoriManager — reusable dialog untuk kelola kategori mapping.
 * Props:
 *   jenis: "piutang" | "utang" | "uang_masuk" | "uang_keluar"
 *   title: string
 *   onClose: () => void
 *
 * Untuk uang_masuk & uang_keluar ada lapisan KEDUA: pemetaan khusus per
 * supplier yang mengalahkan pemetaan kategori. Urutan resolusinya sengaja
 * dicetak di layar (bukan cuma di kode) — pemetaan bertingkat yang tidak
 * menjelaskan dirinya sendiri persis sama tidak bergunanya dengan tidak ada.
 */
export default function KategoriManager({ jenis, title, onClose }) {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [coa, setCoa] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [urutan, setUrutan] = useState([]);
  const [dukungSupplier, setDukungSupplier] = useState(false);
  const [showSupForm, setShowSupForm] = useState(false);
  const bolehTulis = can("finance_coa", "create");
  const bolehHapus = can("finance_coa", "delete");

  const validCategoryFor = useCallback(() => {
    if (jenis === "piutang" || jenis === "uang_masuk") return ["pendapatan"];
    if (jenis === "utang") return ["persediaan", "aset_tetap", "beban", "hpp"];
    if (jenis === "uang_keluar") return ["persediaan", "aset_tetap", "beban", "hpp", "utang", "modal"];
    return [];
  }, [jenis]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [k, c, s] = await Promise.all([
        api.get(`/finance/kategori-map?jenis=${jenis}`),
        api.get("/finance/coa"),
        // Daftar supplier lewat pintu Finance sendiri (data master saja) supaya
        // pemegang izin Finance tidak perlu diberi izin Inventory penuh.
        api.get("/finance/kategori-map/supplier-options").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(k.data.items || []);
      setOverrides(k.data.supplier_overrides || []);
      setDukungSupplier(!!k.data.dukung_supplier);
      setUrutan(k.data.urutan_resolusi || []);
      setSuppliers(s.data.items || []);
      setCoa((c.data || []).filter(a => validCategoryFor().includes(a.category)));
    } catch (e) { setLoadError(e); toast.error("Gagal memuat kategori"); }
    finally { setLoading(false); }
  }, [jenis, validCategoryFor]);
  useEffect(() => { load(); }, [load]);

  const deleteItem = async (it) => {
    if (!it.id) return toast.error("Default kategori tidak bisa dihapus. Buat custom untuk override.");
    if (!window.confirm(`Hapus kategori '${it.kategori}'?`)) return;
    try { await api.delete(`/finance/kategori-map/${it.id}`); toast.success("Terhapus ✓"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const hapusOverride = async (ov) => {
    if (!window.confirm(`Hapus pemetaan khusus '${ov.kategori}' + supplier ${ov.supplier_nama}?\n\nSetelah dihapus, kategori itu kembali memakai pemetaan kategori biasa.`)) return;
    try { await api.delete(`/finance/kategori-map/${ov.id}`); toast.success("Pemetaan khusus dihapus ✓"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  // Backend sudah mengurut (urut nama) SEBELUM dikirim — daftar ini tidak
  // dipotong (bukan pola `.to_list(N)` tanpa filter), jadi urutan bawaan
  // tetap dipertahankan sampai header diklik, tanpa perlu BatasDaftarNotice.
  const katSort = useSortState(null);
  const sortedItems = sortRows(items, katSort.sortKey, katSort.sortDir, {
    kategori: (it) => it.kategori || "",
    coa: (it) => it.coa_code || "",
  });
  const ovSort = useSortState(null);
  const sortedOverrides = sortRows(overrides, ovSort.sortKey, ovSort.sortDir, {
    kategori: (ov) => ov.kategori || "",
    supplier: (ov) => ov.supplier_nama || "",
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-emerald-600" /> {title}</h3>
            <p className="text-xs text-slate-500">Kelola pilihan kategori & link ke COA</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <div className="flex gap-2 mb-3">
            <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="kat-add-new" className="h-9 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Tambah Kategori
            </button>
            <button onClick={() => setShowImport(true)} data-testid="kat-bulk-import" className="h-9 px-3 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
              <UploadIcon className="w-4 h-4" /> Import CSV
            </button>
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase text-slate-500">
                <tr>
                  <SortTh label="Kategori" sortKeyName="kategori" sortKey={katSort.sortKey} sortDir={katSort.sortDir} onSort={katSort.toggle} testid="kat-sort-kategori" />
                  <SortTh label="COA Link" sortKeyName="coa" sortKey={katSort.sortKey} sortDir={katSort.sortDir} onSort={katSort.toggle} testid="kat-sort-coa" />
                  <th className="px-3 py-2 text-center">Source</th>
                  <th className="px-3 py-2 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada kategori" varian="tabel" kolom={4} data-testid="loading-kategori">
                  {sortedItems.map((it, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-medium text-slate-900">{it.kategori}</td>
                      <td className="px-3 py-2 text-slate-700 text-xs">{it.coa_code} · {it.coa_name}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${it.source === "custom" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {it.source === "custom" ? "Custom" : "Default"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {it.source === "custom" && (
                          <>
                            <button onClick={() => { setEditing(it); setShowForm(true); }} className="text-slate-500 hover:text-slate-900 mr-2"><Pencil className="w-3.5 h-3.5" /></button>
                            <button onClick={() => deleteItem(it)} data-testid={`kat-del-${it.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </LoadingState>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">💡 Default kategori tidak bisa dihapus. Buat custom untuk menambah pilihan baru atau override link COA-nya.</p>

          {dukungSupplier && (
            <div className="mt-6 border-t border-slate-100 pt-5" data-testid="kat-supplier-section">
              <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
                <div>
                  <p className="font-display font-bold text-sm text-slate-900 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-emerald-600" /> Pemetaan Khusus Supplier
                  </p>
                  <p className="text-xs text-slate-500">Opsional — dipakai hanya kalau suppliernya dipilih saat mencatat transaksi.</p>
                </div>
                {bolehTulis && (
                  <button onClick={() => setShowSupForm(true)} data-testid="kat-sup-add"
                    className="h-9 px-3 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> Tambah Pemetaan Supplier
                  </button>
                )}
              </div>

              {/* Urutan resolusi dicetak dari backend (bukan disalin ke JS) supaya
                  layar dan mesinnya tidak bisa berbeda cerita. */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3" data-testid="kat-urutan-resolusi">
                <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <ListOrdered className="w-3.5 h-3.5" /> Urutan penentuan akun
                </p>
                <ol className="text-[11px] text-slate-600 space-y-0.5 list-decimal pl-4">
                  {urutan.map((u, i) => <li key={i}>{u}</li>)}
                </ol>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase text-slate-500">
                    <tr>
                      <SortTh label="Kategori" sortKeyName="kategori" sortKey={ovSort.sortKey} sortDir={ovSort.sortDir} onSort={ovSort.toggle} testid="kat-sup-sort-kategori" />
                      <SortTh label="Supplier" sortKeyName="supplier" sortKey={ovSort.sortKey} sortDir={ovSort.sortDir} onSort={ovSort.toggle} testid="kat-sup-sort-supplier" />
                      <th className="px-3 py-2 text-left">COA Link</th>
                      <th className="px-3 py-2 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-400">
                        Belum ada pemetaan khusus supplier — semua kategori memakai pemetaan umum di atas.
                      </td></tr>
                    )}
                    {sortedOverrides.map(ov => (
                      <tr key={ov.id} data-testid={`kat-sup-row-${ov.id}`} className={`border-b border-slate-50 hover:bg-slate-50/50 ${ov.is_active ? "" : "opacity-60"}`}>
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {ov.kategori}
                          {/* Baris nonaktif tidak ikut resolusi sama sekali —
                              menampilkannya tanpa tanda sama saja berbohong. */}
                          {!ov.is_active && <span className="ml-1.5 px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-500 text-[10px]">nonaktif</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-700 text-xs">
                          {ov.supplier_nama}
                          {ov.supplier_status !== "aktif" && (
                            <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[10px]">
                              <AlertTriangle className="w-3 h-3" /> {ov.supplier_status}
                            </span>
                          )}
                          {ov.supplier_catatan && <p className="text-[10px] text-amber-700 mt-0.5">{ov.supplier_catatan}</p>}
                        </td>
                        <td className="px-3 py-2 text-slate-700 text-xs">{ov.coa_code} · {ov.coa_name}</td>
                        <td className="px-3 py-2 text-right">
                          {bolehHapus && (
                            <button onClick={() => hapusOverride(ov)} data-testid={`kat-sup-del-${ov.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                💡 Kategori yang tidak punya baris di sini tetap memakai pemetaan umum — tidak ada yang berubah untuk transaksi tanpa supplier.
              </p>
            </div>
          )}
        </div>
        {showForm && <FormKategori jenis={jenis} editing={editing} coa={coa} onClose={() => { setShowForm(false); setEditing(null); load(); }} />}
        {showImport && <BulkImportDialog jenis={jenis} coa={coa} onClose={() => { setShowImport(false); load(); }} />}
        {showSupForm && (
          <FormPemetaanSupplier jenis={jenis} coa={coa} kategoriList={items} suppliers={suppliers}
            onClose={() => { setShowSupForm(false); load(); }} />
        )}
      </div>
    </div>
  );
}

/* Form pemetaan (kategori + supplier) → COA.
 * Menampilkan "sebelum" (akun yang berlaku sekarang untuk pasangan ini) supaya
 * pengguna tahu persis apa yang sedang ia geser, bukan sekadar mengisi 3 kotak. */
function FormPemetaanSupplier({ jenis, coa, kategoriList, suppliers, onClose }) {
  const [f, setF] = useState({ kategori: "", supplier_id: "", coa_code: "" });
  const [busy, setBusy] = useState(false);
  const [sebelum, setSebelum] = useState(null);

  useEffect(() => {
    if (!f.kategori) { setSebelum(null); return undefined; }
    let batal = false;
    const q = new URLSearchParams({ jenis, kategori: f.kategori });
    if (f.supplier_id) q.set("supplier_id", f.supplier_id);
    api.get(`/finance/kategori-map/resolusi?${q.toString()}`)
      .then(r => { if (!batal) setSebelum(r.data); })
      .catch(() => { if (!batal) setSebelum(null); });
    return () => { batal = true; };
  }, [jenis, f.kategori, f.supplier_id]);

  const submit = async () => {
    if (!f.kategori || !f.supplier_id || !f.coa_code) return toast.error("Kategori, supplier & COA wajib diisi");
    setBusy(true);
    try {
      const r = await api.post("/finance/kategori-map", { jenis, ...f });
      toast.success(r.data.message || "Pemetaan tersimpan ✓");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  // `dangerous` — 3 pilihan (kategori/supplier/COA) yang menentukan pemetaan
  // akun; kehilangannya lewat klik/Escape tak sengaja berarti memilih ulang.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, dangerous: true });
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" data-testid="kat-sup-form">
        <h4 className="font-display font-bold text-base mb-1">Pemetaan Khusus Supplier</h4>
        <p className="text-xs text-slate-500 mb-4">Berlaku hanya untuk kombinasi kategori + supplier ini. Kategori tanpa supplier tidak terpengaruh.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Kategori</label>
            <select value={f.kategori} onChange={e => setF({ ...f, kategori: e.target.value })} data-testid="kat-sup-kategori" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none">
              <option value="">Pilih kategori...</option>
              {kategoriList.map(k => <option key={k.kategori} value={k.kategori}>{k.kategori}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Supplier</label>
            <select value={f.supplier_id} onChange={e => setF({ ...f, supplier_id: e.target.value })} data-testid="kat-sup-supplier" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none">
              <option value="">Pilih supplier...</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.nama}</option>)}
            </select>
            {suppliers.length === 0 && <p className="text-[10px] text-amber-700 mt-1">Belum ada supplier aktif di Master Supplier.</p>}
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Uang mendarat di COA</label>
            <select value={f.coa_code} onChange={e => setF({ ...f, coa_code: e.target.value })} data-testid="kat-sup-coa" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none">
              <option value="">Pilih akun COA...</option>
              {coa.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
            </select>
          </div>
          {sebelum && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid="kat-sup-preview">
              <p className="font-semibold text-slate-700">Sekarang mendarat di:{" "}
                {sebelum.coa_code ? <span className="font-mono">{sebelum.coa_code} · {sebelum.coa_name}</span> : "belum dipetakan"}
              </p>
              <p className="text-slate-500 mt-0.5">Sumber: <b>{sebelum.sumber_label}</b> — {sebelum.alasan}</p>
            </div>
          )}
        </div>
        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="kat-sup-guard" /></div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy} data-testid="kat-sup-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}

function BulkImportDialog({ jenis, coa, onClose }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const preview = csv.trim().split("\n").filter(Boolean).slice(0, 8);
  const totalLines = csv.trim().split("\n").filter(Boolean).length;

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/finance/kategori-map/template.csv?jenis=${jenis}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Gagal download");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `template-kategori-${jenis}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template CSV terdownload ✓");
    } catch { toast.error("Gagal download template"); }
  };

  const submit = async () => {
    if (!csv.trim()) return toast.error("Paste CSV dulu");
    setBusy(true);
    try {
      const r = await api.post("/finance/kategori-map/bulk-import", { jenis, csv_text: csv });
      setResult(r.data);
      toast.success(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal import"); }
    finally { setBusy(false); }
  };

  const sampleCoa = coa.slice(0, 3).map(c => `${c.code}`).join(", ") || "6110, 4110, 2000";

  // `dangerous` — CSV yang sudah di-paste (bisa banyak baris) hilang kalau
  // modal tertutup tak sengaja sebelum di-import. `result` non-null berarti
  // import sudah jalan — disabled supaya Ctrl+Enter tidak memicu import ulang.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: busy || !csv.trim() || !!result, dangerous: true });

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <h4 className="font-display font-bold text-base">Import Kategori dari CSV</h4>
            <p className="text-xs text-slate-500 mt-0.5">Format: <code className="bg-slate-100 px-1 rounded font-mono">kategori,coa_code</code> per baris.</p>
          </div>
          <button onClick={downloadTemplate} data-testid="kat-tpl-download" className="shrink-0 h-8 px-3 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs font-medium inline-flex items-center gap-1.5">
            <DownloadIcon className="w-3.5 h-3.5" /> Template CSV
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">CSV Data</label>
            <textarea value={csv} onChange={e => setCsv(e.target.value)} data-testid="kat-import-csv" rows={8} placeholder={`kategori,coa_code\nSewa Gudang,6120\nGaji Karyawan Kontrak,6130\nBiaya Kirim,6900`} className="w-full p-3 border border-slate-200 rounded-lg text-sm font-mono focus:border-emerald-500 outline-none resize-none" />
            <p className="text-[10px] text-slate-400 mt-1">Header row (baris pertama) opsional · Contoh COA code aktif: {sampleCoa}</p>
          </div>
          {totalLines > 0 && !result && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-[11px] font-semibold text-slate-600 mb-1.5">Preview {preview.length} dari {totalLines} baris</p>
              <ul className="text-xs text-slate-700 font-mono space-y-0.5 max-h-24 overflow-y-auto">
                {preview.map((l, i) => <li key={i} className="truncate">{l}</li>)}
              </ul>
            </div>
          )}
          {result && (
            <div className={`border rounded-lg p-3 text-sm ${result.errors.length > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
              <p className="font-semibold">{result.message}</p>
              <div className="mt-1.5 space-y-0.5 text-xs">
                <p>✓ <b>{result.inserted}</b> kategori baru</p>
                <p>⊘ <b>{result.skipped}</b> skip (sudah ada)</p>
                <p>✕ <b>{result.errors.length}</b> error</p>
              </div>
              {result.errors.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer font-medium">Lihat error</summary>
                  <ul className="mt-1 pl-4 list-disc space-y-0.5">
                    {result.errors.slice(0, 10).map((e, i) => <li key={i}>Baris {e.line}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        {guard.confirming && (
          <div className="-mx-0"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="kat-import-guard" /></div>
        )}
        <div className="p-5 border-t border-slate-100 flex gap-2">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Tutup</button>
          {!result && (
            <button onClick={submit} disabled={busy || !csv.trim()} data-testid="kat-import-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "Importing..." : `Import ${totalLines} Baris`}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormKategori({ jenis, editing, coa, onClose }) {
  const [f, setF] = useState({
    kategori: editing?.kategori || "",
    coa_code: editing?.coa_code || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.kategori.trim() || !f.coa_code) return toast.error("Nama & COA wajib diisi");
    setBusy(true);
    try {
      if (editing?.id) {
        await api.put(`/finance/kategori-map/${editing.id}`, f);
        toast.success("Kategori diperbarui ✓");
      } else {
        await api.post("/finance/kategori-map", { jenis, ...f });
        toast.success("Kategori ditambahkan ✓");
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h4 className="font-display font-bold text-base mb-4">{editing ? "Edit Kategori" : "Tambah Kategori"}</h4>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Nama Kategori</label>
            <input value={f.kategori} onChange={e => setF({ ...f, kategori: e.target.value })} data-testid="kat-form-nama" placeholder="Contoh: Konsinyasi Reseller" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Link ke COA</label>
            <select value={f.coa_code} onChange={e => setF({ ...f, coa_code: e.target.value })} data-testid="kat-form-coa" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none">
              <option value="">Pilih akun COA...</option>
              {coa.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy} data-testid="kat-form-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}
